/**
 * @ComponentFor CTABlockViewModel
 */

import React from 'react';
import CTABlockViewModel from 'Shared/Blocks/CTABlock/CTABlockViewModel.type';
import { isCompact } from 'Shared/Viewport';
import connect from 'Shared/connect';
import { styled } from '@glitz/react';
import { LinkButton, ButtonTheme } from 'Shared/TextButtons';
import { AspectRatioInner, AspectRatioOuter, OverlayNotABackdrop } from 'Shared/SharedComponents/atoms/AspectRatio';
import { H2, ResponsiveSize, TextBlockSmall } from 'Shared/SharedComponents/atoms/Typography';
import { createGridClasses, createOffsetGridClassLarge } from 'Shared/grid';
import { setImageBackground, Preset } from 'Shared/image-background';
import { GridWrap } from 'Shared/PageLayout';
import { selectDeviceImage } from 'Shared/utils';
import { calculateTopPadding, Ratio } from 'Shared/AspectRatio';
import VideoPlayer from 'Shared/Video/VideoPlayer'
import * as style from 'Shared/Style';
import { pushState } from '@polarnopyret/scope/index';

const Wrap = styled(AspectRatioOuter, {
  ['@media ' + style.mediaUpToLarge]: {
    marginBottom: '8px',
  },
})

const WrapInner = styled(AspectRatioInner, {
  background: {
    size: 'cover',
    position: 'center center',
  },
})

const Label = styled(TextBlockSmall, {
  marginBottom: '16px',
  letterSpacing: '0.01em',
  textAlign: 'center',
  color: style.colors.monochrome.white,
})

const Heading = styled(H2, {
  textAlign: 'center',
  color: style.colors.monochrome.white,
  alignItems: 'center',
})

const SmallSizeHeading = styled(Heading, {
  padding: { x: '40px' }
})

const SmallSizeOverlay = styled(OverlayNotABackdrop, {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexDirection: 'column'
})

const ButtonOverlay = styled(OverlayNotABackdrop, {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'flex-end',
  top: 'auto',
  bottom: '24px',
  gap: '12px',
  ['@media ' + style.mediaMinLarge]: {
    gap: '16px',
  },
})

const FullWidthGridWrap = styled(GridWrap, {
  height: '100%'
})

const FullWidthLabelWrap = styled.div({
  alignSelf: 'end'
})

const FullWidthHeadingWrap = styled.div({
  alignSelf: 'start'
})

const FullWidthSecondaryHeading = styled.span({
  display: 'inline-block'
})

const SmallSizeSecondaryHeading = styled.span({
  display: 'block'
})

const CallToActionBlock = connect((state) => {
  return {
    currentBreakpoint: state.currentBreakpoint,
  };
})((props: CTABlockViewModel & { currentBreakpoint: number }) => {
  const isMobile = isCompact(props.currentBreakpoint);
  const hasVideo = props.videoSources?.length > 0;
  const fullWidth = !props.width && props.layout === 'default';

  const headingSize = fullWidth ? ResponsiveSize.D96_M40 : (props.layout !== 'default' ? ResponsiveSize.D48_M32 : ResponsiveSize.D64_M32);
  const backgroundImage = selectDeviceImage(props.layout === 'list' ? props.backgroundListImage : props.backgroundImage, props.backgroundImageSmallScreen, isMobile);
  const firstBtnUrl = props.buttons && props.buttons[0].url;

  const imageRatio = () => {
    if (isMobile) {
      if (fullWidth) {
        return Ratio.TwelveToTwentyThree;
      }

      return Ratio.ThreeToFour;
    }

    if (fullWidth) {
      return Ratio.SixteenToNine;
    }

    if (props.layout === 'list') {
      return Ratio.ThreeToTwo;
    }

    return Ratio.ThreeToFour;
  }

  return (
    <Wrap
      css={{
        paddingTop: calculateTopPadding(imageRatio()),
        ...(firstBtnUrl && {
          cursor: 'pointer'
        })
      }}
      onClick={(e) => {
        if (!firstBtnUrl) {
          return;
        }

        const targetElement = e.target as HTMLElement;
        if (targetElement.nodeName === 'A') {
          return;
        }

        if (targetElement.nodeName === 'SPAN') {
          const targetParent = targetElement.parentElement as HTMLElement;
          if (targetParent.nodeName === 'A') {
            return;
          }
        }

        pushState(firstBtnUrl);
      }}
    >
      <WrapInner
        css={{
          background: {
            image: !hasVideo
              ? setImageBackground(backgroundImage?.url, (fullWidth ? Preset.FullWidth : Preset.HalfWidth))
              : undefined,
          },
          ...(!fullWidth && {
            borderRadius: '8px'
          })
        }}
      >
        {hasVideo && (
          <OverlayNotABackdrop>
            <VideoPlayer
              autoPlay
              loop
              playsInline
              coverContent
              muted
              isMobile={isMobile}
              sources={props.videoSources}
            />
          </OverlayNotABackdrop>
        )}
        {fullWidth ? (
          <OverlayNotABackdrop>
            <FullWidthGridWrap className="grid">
              <FullWidthLabelWrap className={createGridClasses(12, 4, createOffsetGridClassLarge(5))}>
                <Label upperCase>
                  {props.label}
                </Label>
              </FullWidthLabelWrap>
              <FullWidthHeadingWrap className={createGridClasses(12, 8, createOffsetGridClassLarge(3))}>
                <Heading fontSize={headingSize} >
                  {props.heading}
                  {props.headingSecondary && (
                    <FullWidthSecondaryHeading>{props.headingSecondary}</FullWidthSecondaryHeading>
                  )}
                </Heading>
              </FullWidthHeadingWrap>
            </FullWidthGridWrap>
          </OverlayNotABackdrop>
        ) : (
          <SmallSizeOverlay>
            <Label upperCase>
              {props.label}
            </Label>
            <SmallSizeHeading fontSize={headingSize}>
              {props.heading}
              {props.headingSecondary && (
                <SmallSizeSecondaryHeading>{props.headingSecondary}</SmallSizeSecondaryHeading>
              )}
            </SmallSizeHeading>
          </SmallSizeOverlay>
        )}
        <ButtonOverlay>
          {props.buttons?.map((button, index) => (
            <LinkButton
              key={index}
              to={button.url}
              text={button.text ?? ''}
              title={button.title ?? ''}
              theme={
                button.background === 'Transparent' ? ButtonTheme.TransparentWhite : ButtonTheme.WhiteToTransparent
              }
              display={'flex'}
            />
          ))}
        </ButtonOverlay>
      </WrapInner>
    </Wrap>
  );
});

export default CallToActionBlock;
